import queryString from "query-string";
import { AccountingUtils } from "revlock-accounting";
import UserActions from "redux/actions/user.actions";
import * as ChargebeeUtils from "utils/ChargebeeUtils";
import { setCookie } from "rest/API";
import {
    getCurrentServiceProfile,
    parseOrgIdFromAppPath,
    ensureOrgProfile
} from "utils/WebUtils";

/** Set with upgradeRedirect "/" before wrong-SPA profile hop; cleared in app.component. */
export const SSO_COLD_START_FORWARD_HOME_COOKIE = "ssoColdStartForwardHome";

/** Minutes — must survive Billing detour before target SPA loads index.html. */
export const SSO_COLD_START_FORWARD_HOME_TTL_MIN = 5;

function resolveOrg(userOrganizations, orgId) {
    return (
        orgId &&
        userOrganizations.find((o) => String(o.id) === String(orgId))
    );
}

function matchesServiceProfile(org, serviceProfile) {
    return org && (!org.profile || org.profile === serviceProfile);
}

/**
 * Known tenant from Billing return, deep link, or site-switcher upgradeRedirect.
 * Priority: ?clientId= → path org → upgradeRedirect cookie.
 */
export function resolveExplicitColdStartTarget(cookies) {
    const search =
        (typeof window !== "undefined" &&
            window.location &&
            window.location.search) ||
        "";
    const { clientId } = queryString.parse(search);
    const pathOrgId =
        typeof window !== "undefined" &&
        window.location &&
        window.location.pathname
            ? parseOrgIdFromAppPath(window.location.pathname)
            : null;
    const upgradeRedirectPath = cookies && cookies.get("upgradeRedirect");
    const upgradeRedirectOrgId = upgradeRedirectPath
        ? parseOrgIdFromAppPath(upgradeRedirectPath)
        : null;

    if (clientId) {
        return { orgId: String(clientId), source: "clientId" };
    }
    if (pathOrgId) {
        return { orgId: String(pathOrgId), source: "pathOrgId" };
    }
    if (upgradeRedirectOrgId) {
        return { orgId: upgradeRedirectOrgId, source: "upgradeRedirect" };
    }
    return null;
}

/**
 * Legacy-style org pick (cookie → production → test → first). Used only to learn
 * a target SPA profile when the same-profile candidate list is empty — not for SSO evaluate.
 */
export function pickLegacyStyleOrg(userOrganizations, cookieOrgId) {
    if (!userOrganizations || !userOrganizations.length) {
        return null;
    }
    const fromCookie = resolveOrg(userOrganizations, cookieOrgId);
    if (fromCookie) {
        return fromCookie;
    }
    const productionOrg = userOrganizations.find(
        (o) => !AccountingUtils.isTestId(o.id)
    );
    if (productionOrg) {
        return productionOrg;
    }
    const testOrg = userOrganizations.find((o) =>
        AccountingUtils.isTestId(o.id)
    );
    if (testOrg) {
        return testOrg;
    }
    return userOrganizations[0];
}

/**
 * Consumes upgradeRedirect after a profile-hop index.html load (app.component).
 * Returns true when history.push ran.
 *
 * If upgradeRedirect expired but ssoColdStartForwardHome remains (Billing
 * detour), treat target as "/" so the hop still completes.
 */
export function consumeUpgradeRedirectForward({
    redirectLocation,
    currentPath,
    redirectPath,
    getCookie,
    removeCookie,
    pushHistory
}) {
    const onForwardPath =
        currentPath === redirectPath || currentPath === "/";
    const ssoColdStartForwardHome = getCookie(
        SSO_COLD_START_FORWARD_HOME_COOKIE
    );

    if (!onForwardPath) {
        // Drop stale marker so it cannot authorize a later unrelated "/" hop.
        if (ssoColdStartForwardHome) {
            removeCookie(SSO_COLD_START_FORWARD_HOME_COOKIE);
        }
        return false;
    }

    const location =
        redirectLocation || (ssoColdStartForwardHome ? "/" : null);
    if (!location) {
        return false;
    }

    if (redirectLocation) {
        removeCookie("upgradeRedirect");
    }
    if (ssoColdStartForwardHome) {
        removeCookie(SSO_COLD_START_FORWARD_HOME_COOKIE);
    }

    if (location !== "/" || ssoColdStartForwardHome) {
        pushHistory(location);
        return true;
    }
    return false;
}

/**
 * Ambiguous cold-start list: cookie → test org → remaining, same SPA profile only.
 */
export function buildSameProfileColdStartCandidates(
    userOrganizations,
    cookieOrgId,
    serviceProfile
) {
    const candidates = [];
    const seen = new Set();
    const pushUnique = (org) => {
        if (!matchesServiceProfile(org, serviceProfile) || !org || !org.id) {
            return;
        }
        const key = String(org.id);
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        candidates.push(org);
    };

    if (cookieOrgId) {
        pushUnique(resolveOrg(userOrganizations, cookieOrgId));
    }
    pushUnique(
        userOrganizations.find((o) => AccountingUtils.isTestId(o.id))
    );
    userOrganizations.forEach((org) => pushUnique(org));
    return candidates;
}

/**
 * Apply org on this SPA, or hard-nav via ensureOrgProfile when profiles differ.
 * Cross-profile Redux apply is what caused a/b redirect loops.
 */
export async function tryApplyOrgForColdStart(org, serviceProfile, opts) {
    if (!org) {
        return false;
    }
    if (!matchesServiceProfile(org, serviceProfile)) {
        console.log(
            `[SSO cold-start] org ${org.id} profile ${org.profile} != SPA ${serviceProfile}, ensureOrgProfile`
        );
        await ensureOrgProfile(org.id, {
            orgProfile: org.profile,
            url: `/${org.id}/dashboards`
        });
        return false;
    }
    return UserActions.setCurrentOrganization(org, undefined, opts);
}

/**
 * SSO-active cold start only (caller already probed enforcementActive).
 * Explicit target → one evaluate; else same-profile walk.
 */
export async function selectOrganizationForSsoColdStart({
    userOrganizations,
    cookies,
    hasCurrentOrg
}) {
    const serviceProfile = getCurrentServiceProfile();
    const explicit = resolveExplicitColdStartTarget(cookies);

    if (explicit) {
        const explicitOrg = resolveOrg(userOrganizations, explicit.orgId);
        console.log(
            `[SSO cold-start] explicit target ${explicit.orgId} from ${explicit.source}`
        );
        if (explicitOrg) {
            const ok = await tryApplyOrgForColdStart(explicitOrg, serviceProfile, {
                skipBillingRedirect: false
            });
            console.log(
                `[SSO cold-start] explicit target ${explicit.orgId} allowed=${ok}`
            );
        } else {
            console.log(
                `[SSO cold-start] explicit target ${explicit.orgId} not in user org list`
            );
        }
        return;
    }

    const cookieOrgId = cookies && cookies.get("orgId");
    const candidates = buildSameProfileColdStartCandidates(
        userOrganizations,
        cookieOrgId,
        serviceProfile
    );

    console.log(
        `[SSO cold-start] ambiguous walk, ${candidates.length} same-profile candidate(s) on SPA ${serviceProfile}`
    );

    // Wrong SPA (e.g. Billing → /b while all tenants live on /a): same-profile
    // filter is empty. Soft-nav to tenants' profile so the next SPA can walk.
    if (candidates.length === 0) {
        const fallback = pickLegacyStyleOrg(userOrganizations, cookieOrgId);
        if (fallback && fallback.id) {
            console.log(
                `[SSO cold-start] no same-profile candidates; ensureOrgProfile via org ${fallback.id}`
            );
            // Soft "/" — not /{orgId}/dashboards (avoids explicit SSO/Billing)
            // and not /dashboards (avoids index.js treating it as clientId).
            // Same cookie helper as ensureOrgProfile; hop only after marker is set.
            setCookie(
                SSO_COLD_START_FORWARD_HOME_COOKIE,
                "1",
                "/",
                SSO_COLD_START_FORWARD_HOME_TTL_MIN
            );
            await ensureOrgProfile(fallback.id, {
                orgProfile: fallback.profile,
                url: "/",
                forceUpgradeRedirect: true
            });
        }
        return;
    }

    for (let i = 0; i < candidates.length; i++) {
        if (hasCurrentOrg && hasCurrentOrg()) {
            return;
        }
        const last = i === candidates.length - 1;
        const ok = await tryApplyOrgForColdStart(candidates[i], serviceProfile, {
            skipBillingRedirect: !last
        });
        if (ok) {
            console.log(
                `[SSO cold-start] ambiguous walk selected org ${candidates[i].id}`
            );
            return;
        }
    }
}

/**
 * When SSO enforcement is on for this org and profiles differ, hard-nav to the
 * correct SPA instead of applying the org in Redux (avoids a/b loops + 426).
 * Returns true if caller may proceed with setCurrentOrganization.
 */
export async function canApplyOrgOnCurrentSpa(org) {
    if (!org || !org.id || !ChargebeeUtils.isChargebeeUser()) {
        return true;
    }
    const enforcementOn = await UserActions.isSsoEnforcementActiveCached([
        org
    ]);
    const serviceProfile = getCurrentServiceProfile();
    if (enforcementOn && org.profile && org.profile !== serviceProfile) {
        console.log(
            `[SSO profile-gate] org ${org.id} profile ${org.profile} != SPA ${serviceProfile}, ensureOrgProfile`
        );
        ensureOrgProfile(org.id, {
            orgProfile: org.profile,
            url:
                (typeof window !== "undefined" &&
                    window.location &&
                    window.location.pathname + window.location.search) ||
                `/${org.id}/dashboards`
        });
        return false;
    }
    return true;
}
